<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import { useUserStore } from '@/authentication/user.store'
import { useContentAccessStore } from '@/content_access/content_access.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { actionPermissions, ACTION_TYPES } from '@/util/access_visibility'
import { getClipboardText } from '@/util/copypaste_helpers'

interface CustomClipboardEvent extends ClipboardEvent {
  detail?: {
    fromButton?: boolean;
    stringToCopy?: string;
    copyBlocked?: boolean;
  };
}

const COPY_DEBOUNCE_MS = 500

const props = defineProps({
  type: {
    type: String,
    default: ''
  },
  title: {
    type: String,
    default: ''
  },
  uuid: {
    type: String,
    default: ''
  },
  level: {
    type: String,
    default: ''
  },
  // Pre-existing Vue 3 pages never gated copying, so only the pages that need Vue 2's copy entitlements opt in.
  gateCopy: {
    type: Boolean,
    default: false
  },
  demo: {
    type: Boolean,
    default: false
  },
  enforceQuota: {
    type: Boolean,
    default: true
  }
})

const analytics = getAnalyticsTracker()
const { authInfo } = storeToRefs(useUserStore())
const contentAccessStore = useContentAccessStore()
const { contentAccessStatusForPermissions } = storeToRefs(contentAccessStore)

const showBlocker = ref(false)
let timeOfLastCopy = 0

const copyPermissions = computed(() =>
  actionPermissions(
    ACTION_TYPES.COPY,
    props.type,
    { demo: props.demo, uuid: props.uuid, title: props.title },
    authInfo.value,
    contentAccessStatusForPermissions.value
  )
)

function truncateText(text: string) {
    let truncatedText = text
    let words = text.split(' ')
    if (words.length > 40) {
      truncatedText = words
        .slice(0, 40)
        .concat([`...(${words.length - 40} more words)`])
        .join(' ')
    }
    return truncatedText
}

function cancelCopy(evt: CustomClipboardEvent) {
  evt.preventDefault()
  // CopyButton writes the clipboard itself, so it needs telling; a cancelled event alone is not a signal, since the body cancels every copy that reaches it.
  if (evt.detail) evt.detail.copyBlocked = true
  evt.stopPropagation()
}

function isRepeatCopy() {
  const now = Date.now()
  const isRepeat = now - timeOfLastCopy < COPY_DEBOUNCE_MS
  timeOfLastCopy = now
  return isRepeat
}

// Runs while the copy is captured on its way down: an editor (ProseMirror) writes the clipboard in its own copy handler, too early for a cancel on the way back up to undo it.
function cancelBlockedCopy(evt: CustomClipboardEvent) {
  if (!props.gateCopy || !copyPermissions.value.block || !isInnermostWrapperOf(evt)) return
  cancelCopy(evt)
  if (props.enforceQuota && copyPermissions.value.blockQuota) showBlocker.value = true
}

// Attached by hand, not as @copy.capture: Vue skips a second listener for the same event on one element when the event is no newer than its attach time.
const root = ref<HTMLElement>()
onMounted(() => root.value?.addEventListener('copy', cancelBlockedCopy, { capture: true }))

function isInnermostWrapperOf(evt: Event) {
  const target = evt.target instanceof Element ? evt.target : null
  return !target || target.closest('[data-copy-track-wrapper]') === root.value
}

function countCopy() {
  if (!props.enforceQuota) return
  contentAccessStore.incrementCopyCount()
  contentAccessStore.refetchContentAccessReport()
}

function skipRepeatReport(evt: CustomClipboardEvent) {
  // Leaves copyBlocked unset, so a Copy button still writes and confirms the repeat; only its report is skipped.
  evt.preventDefault()
  evt.stopPropagation()
}

function captureCopy(evt: CustomClipboardEvent) {
  if (isRepeatCopy()) {
    if (props.gateCopy) cancelCopy(evt)
    else skipRepeatReport(evt)
    return
  }
  if (props.gateCopy) countCopy()
  const truncatedText = truncateText(evt.detail?.stringToCopy ?? getClipboardText(''))
  analytics.sendEvent(EVENT_NAMES.MISC.COPIED_TEXT, {
    content_access_report_error: contentAccessStore.contentAccessStatus.reportError,
    copy_count: contentAccessStore.copyCount,
    copied_text_type: props.type,
    copied_text_text: truncatedText,
    copied_text_title: props.title,
    copied_text_uuid: props.uuid,
    copied_text_level: props.level,
    from_button: !!(evt.detail && evt.detail.fromButton),
    // Deprecated in Vue 2 but still sent there, so reports built on them keep working.
    type: props.type,
    title: props.title,
    uuid: props.uuid,
    level: props.level,
    text: truncatedText,
    ...(props.type === 'objective' && { obj_level: props.level })
  })
  // Pages nest wrappers (a page around its banner and panels); the innermost one names the copied content.
  evt.stopPropagation()
}
</script>

<template>
<div ref="root" data-copy-track-wrapper @copy="captureCopy">
  <slot></slot>
  <PremiumActionModal v-if="gateCopy" v-model:external-open="showBlocker" :action-permissions="copyPermissions" />
</div>
</template>
